import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { API } from '../config';
import { devLog } from '../utils/devLog';
import { UserForm, UserTable } from './users/UserComponents';

const UserManagementView = ({ plants, token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', email: '', password: '', role: 'operator', assigned_plants: [] });

  const loadUsers = useCallback(async () => {
    try { setLoading(true); const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }); setUsers(res.data);
    } catch (err) { devLog.error('Failed to load users:', err); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingUser) {
        const updateData = { ...formData }; delete updateData.username;
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API}/users/${editingUser.id}`, updateData, { headers: { Authorization: `Bearer ${token}` } });
        alert('User updated!');
      } else {
        await axios.post(`${API}/users`, formData, { headers: { Authorization: `Bearer ${token}` } });
        alert('User created!');
      }
      setShowAddForm(false); setEditingUser(null);
      setFormData({ username: '', email: '', password: '', role: 'operator', assigned_plants: [] });
      await loadUsers();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); } finally { setLoading(false); }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ username: user.username, email: user.email, password: '', role: user.role, assigned_plants: user.assigned_plants || [] });
    setShowAddForm(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try { await axios.delete(`${API}/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } }); alert('User deleted!'); await loadUsers();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || 'Failed')); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">User Management</h2>
            <p className="text-sm text-gray-600 mt-1">Manage users and their plant access</p>
          </div>
          {!showAddForm && (
            <button onClick={() => { setShowAddForm(true); setEditingUser(null); setFormData({ username: '', email: '', password: '', role: 'operator', assigned_plants: [] }); }}
              className="bg-[#156ACA] hover:bg-[#0F5299] text-white font-semibold py-2.5 px-4 sm:px-6 min-h-[44px] rounded-lg shadow-lg transition-colors flex items-center" data-testid="add-user-btn">
              <Plus className="h-5 w-5 mr-2" /> <span className="whitespace-nowrap">Add User</span>
            </button>
          )}
        </div>
      </div>

      {showAddForm && <UserForm formData={formData} setFormData={setFormData} editingUser={editingUser} plants={plants} loading={loading}
        onSubmit={handleSubmit} onCancel={() => { setShowAddForm(false); setEditingUser(null); }} />}

      <UserTable users={users} loading={loading} onEdit={handleEdit} onDelete={handleDelete} token={token} onReload={loadUsers} />
    </div>
  );
};

export default UserManagementView;
